import React, { useState, useEffect, useRef } from 'react'; // Import useRef
import { PrimaryButton } from '../../../components/Buttons';
import { ChevronDown, FileText, ImageIcon, TextCursorInput, Video, XCircle, ArrowLeft } from 'lucide-react';
import { Chip } from "@material-tailwind/react";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import CustomInput from "../../../components/CustomInput";
import useButtonTypes from "../../../components/useButtonTypes";
import { useLocation } from 'react-router-dom';
import axiosInstance from '../../../services/api/axios';
import ConditionBuilder from './ConditionBuilder';
import { toast } from 'react-toastify';
import { Loader2 } from 'lucide-react';


function TemplateForm({
    templateName,
    setTemplateName,
    templateType,
    setTemplateType,
    headerType,
    setHeaderType,
    headerText,
    setHeaderText,
    headerMedia,
    setHeaderMedia,
    body,
    setBody,
    buttons,
    setButtons,
    leadType,
    setLeadType,
    isOpen,
    setIsOpen,
    onTemplateCreate,
    onUpdate,
    dummyValues,
    handleDummyValueChange,
    quillRef,
    handleImageChange,
    selectedImage,
    handleRemoveImage,
    propertyConfigIds = [],
    setPropertyConfigIds,
    onBack,
    onDelete,
    nodeId,
    isInitialNode = false,
    isViewingSavedTemplate = false,
    templateId = null,
    initialData = null,
    sendDate,
    setSendDate,
    isUpdatingTemplate = false,
    isPublishedTemplate = false
}) {
    console.log("buttons", buttons);

    const [showButtonDropdown, setShowButtonDropdown] = useState(false);
    const [showVariableDropdown, setShowVariableDropdown] = useState(false);
    const [selectedLeadTypes, setSelectedLeadTypes] = useState(leadType || []);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(headerMedia?.preview);
    const [showPropertyConfig, setShowPropertyConfig] = useState(true);
    const [propertyConfigs, setPropertyConfigs] = useState([]);
    const [loadingConfigs, setLoadingConfigs] = useState(false);
    const [configDropdownOpen, setConfigDropdownOpen] = useState(false);
    const [selectedPropertyConfigs, setSelectedPropertyConfigs] = useState(propertyConfigIds || []);
    const [showConditionBuilder, setShowConditionBuilder] = useState(false);
    const [conditionString, setConditionString] = useState('');
    const [conditions, setConditions] = useState([]);
    const [selectedFestival, setSelectedFestival] = useState('');
    const [showFestivalDropdown, setShowFestivalDropdown] = useState(false);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isUpdatingPublishedTemplate, setIsUpdatingPublishedTemplate] = useState(false);
    const [selectedLeadStatus, setSelectedLeadStatus] = useState(initialData?.leadStatus || []);
    const [leadStatusDropdownOpen, setLeadStatusDropdownOpen] = useState(false);

    const location = useLocation();
    const { project } = location.state || {};

    const headerOptions = [
        { value: "text", label: "Text (max 60 chars)", icon: <TextCursorInput className="w-4 h-4" /> },
        { value: "image", label: "Image", icon: <ImageIcon className="w-4 h-4" /> },
        { value: "video", label: "Video", icon: <Video className="w-4 h-4" /> },
        { value: "doc", label: "Document", icon: <FileText className="w-4 h-4" /> },
    ];

    const variableOptions = [
        { label: "Name", value: "name" },
        { label: "Number", value: "number" },
        { label: "Date", value: "date" },
        { label: "Location", value: "location" },
    ];

    const leadTypeOptions = [
        { id: 1, value: "COLD_LEAD", label: "Cold" },
        { id: 2, value: "LUKE_WARM_LEAD", label: "Luke Warm" },
        { id: 3, value: "WARM_LEAD", label: "Warm" },
        { id: 4, value: "HOT_LEAD", label: "Hot" },
        { id: 5, value: "CUSTOMER", label: "Customer" },
    ];

    const leadStatusOptions = [
        { 
            value: "NO_INTENT", 
            label: "No Intent", 
            color: "bg-gray-100 text-gray-700 border-gray-200",
            dotColor: "bg-gray-400"
        },
        { 
            value: "LOW_INTENT", 
            label: "Low Intent", 
            color: "bg-yellow-100 text-yellow-800 border-yellow-200",
            dotColor: "bg-yellow-400"
        },
        { 
            value: "HIGH_INTENT", 
            label: "High Intent", 
            color: "bg-orange-100 text-orange-800 border-orange-200",
            dotColor: "bg-orange-400"
        },
        { 
            value: "NEGOTIATION", 
            label: "Negotiation", 
            color: "bg-red-100 text-red-800 border-red-200",
            dotColor: "bg-red-400"
        }
    ];

    const modules = {
        toolbar: [
            ['bold', 'italic', 'strike', 'code'],
        ],
    };

    const formats = [
        'bold', 'italic', 'strike', 'code',
    ];

    const { getTypeComponent } = useButtonTypes();
    const selectedOption = headerOptions.find((opt) => opt.value === headerType);

    const festivals = [
        { id: 1, name: "Diwali", date: "12 Nov 2024" },
        { id: 2, name: "Christmas", date: "25 Dec 2024" },
        { id: 3, name: "New Year", date: "01 Jan 2025" },
        { id: 4, name: "Holi", date: "25 Mar 2024" },
        { id: 5, name: "Eid", date: "10 Apr 2024" },
        { id: 6, name: "Independence Day", date: "15 Aug 2024" },
        { id: 7, name: "Republic Day", date: "26 Jan 2025" },
        { id: 8, name: "Raksha Bandhan", date: "19 Aug 2024" },
        { id: 9, name: "Dussehra", date: "12 Oct 2024" },
        { id: 10, name: "Ganesh Chaturthi", date: "07 Sep 2024" }
    ];

    // Initialize selectedLeadTypes when leadType changes
    useEffect(() => {
        if (leadType && Array.isArray(leadType)) {
            // Ensure all IDs are numbers
            const numericLeadTypes = leadType.map(id =>
                typeof id === 'string' ? parseInt(id, 10) : id
            ).filter(id => !isNaN(id));

            setSelectedLeadTypes(numericLeadTypes);
        } else {
            setSelectedLeadTypes([]);
        }
    }, [leadType]);

    // Initialize selectedLeadStatus from initialData
    useEffect(() => {
        console.log('TemplateForm useEffect - initialData:', initialData);
        console.log('TemplateForm useEffect - initialData.leadStatus:', initialData?.leadStatus);
        if (initialData?.leadStatus) {
            // Ensure leadStatus is always an array
            const leadStatusArray = Array.isArray(initialData.leadStatus) 
                ? initialData.leadStatus 
                : [initialData.leadStatus];
            console.log('Setting selectedLeadStatus to:', leadStatusArray);
            setSelectedLeadStatus(leadStatusArray);
        } else {
            setSelectedLeadStatus([]);
        }
    }, [initialData?.leadStatus]);

    const handleLeadTypeSelect = (leadType, isChecked) => {

        const selectedLeadType = leadTypeOptions.find((option) => option.value === leadType);

        if (selectedLeadType) {
            const leadTypeId = selectedLeadType.id;

            setSelectedLeadTypes((prevSelected) => {
                let newSelected;

                if (isChecked) {

                    newSelected = prevSelected.includes(leadTypeId)
                        ? prevSelected
                        : [...prevSelected, leadTypeId];
                } else {
                    // Remove from array
                    newSelected = prevSelected.filter((id) => id !== leadTypeId);
                }


                setLeadType(newSelected);
                return newSelected;
            });
        } else if (typeof leadType === 'number') {

            setSelectedLeadTypes((prevSelected) => {
                const newSelected = prevSelected.filter((id) => id !== leadType);
                setLeadType(newSelected);
                return newSelected;
            });
        }
    };

    const handleBodyChange = (value) => {
        if (value.length <= 1024) {
            setBody(value);
        }
    };

    const handleTemplateNameChange = (e) => {
        setTemplateName(e.target.value);
    };

    const handleHeaderTypeChange = (type) => {
        setHeaderType(type);
        setIsOpen(false);
    };

    const handleHeaderTextChange = (e) => {
        if (e.target.value.length <= 60) {
            setHeaderText(e.target.value);
        }
    };

    useEffect(() => {
        // Clean up the old preview URL if it exists
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
        }


        if (headerMedia?.file) {
            setPreviewUrl(URL.createObjectURL(headerMedia.file));
        }

        return () => {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [headerMedia]);

    const handleButtonChange = (index, newData) => {
        const newButtons = [...buttons];
        newButtons[index] = newData;
        setButtons(newButtons);
    };

    const addButton = (type) => {

        const buttonLimits = {
            'visit_website': 2,
            'call_phone_number': 1,
            'copy_offer_code': 1
        };

        const existingCount = buttons.filter(btn => btn.typeOfAction === type).length;


        if (buttonLimits[type] && existingCount >= buttonLimits[type]) {
            alert(`Maximum limit of ${buttonLimits[type]} ${type.replace('_', ' ')} button(s) reached`);
            return;
        }

        // Check total button limit (10 buttons maximum)
        if (buttons.length >= 10) {
            alert('Maximum limit of 10 buttons reached');
            return;
        }

        setShowButtonDropdown(false);
        let newButton = { type: 'quick_reply', label: 'Marketing Opt-out', typeOfAction: type };
        if (type === 'visit_website') newButton = { type: 'visit_website', buttonText: '', websiteURL: '', typeOfAction: type };
        if (type === 'site_visit') newButton = {
            type: 'visit_website',
            buttonText: 'Schedule Site Visit',
            websiteURL: 'https://app.buildvizer.com/site-visit?userId={userId}&projectId={projectId}',
            typeOfAction: type
        };
        if (type === 'call_phone_number') newButton = { type: 'call_phone_number', buttonText: '', country_code: '', phoneNumber: '', typeOfAction: type };
        if (type === 'copy_offer_code') newButton = { type: 'copy_offer_code', buttonText: '', offerCode: '', typeOfAction: type };
        if (type === 'property_config') newButton = { type: 'quick_reply', buttonText: '', configId: null, typeOfAction: type, label: 'Interested in Property Configuration' };
        if (type === 'custom') newButton = { type: 'quick_reply', buttonText: '', typeOfAction: type, label: '' };
        setButtons(prev => [...prev, newButton]);
    };

    const isButtonTypeAtLimit = (type) => {
        const buttonLimits = {
            'visit_website': 2,
            'call_phone_number': 1,
            'copy_offer_code': 1
        };

        if (!buttonLimits[type]) return false;

        const existingCount = buttons.filter(btn => btn.typeOfAction === type).length;
        return existingCount >= buttonLimits[type];
    };


    const isTotalButtonLimitReached = () => {
        return buttons.length >= 10;
    };

    const removeButton = (indexToRemove) => {
        setButtons(buttons.filter((_, index) => index !== indexToRemove));
    };

    const insertVariable = (variableValue) => {
        const quill = quillRef.current.getEditor();
        if (quill) {
            const range = quill.getSelection(true);
            const position = range ? range.index : 0;
            quill.insertText(position, `{{${variableValue}}}`, 'bold');
            quill.setSelection(position + variableValue.length + 4);
            handleBodyChange(quill.root.innerHTML);
            handleDummyValueChange(variableValue, '');
        }
    };

    const toggleButtonDropdown = () => {
        setShowButtonDropdown((prev) => !prev);
        setShowVariableDropdown(false);
    };

    const toggleVariableDropdown = () => {
        setShowVariableDropdown((prev) => !prev);
        setShowButtonDropdown(false);
    };

    const toggleDropdown = () => {
        // Prevent dropdown from opening during updates
        if (isUpdatingPublishedTemplate || isUpdatingTemplate) {
            return;
        }
        setDropdownOpen(!dropdownOpen);
    };

    const toggleLeadStatusDropdown = () => {
        // Prevent dropdown from opening during updates
        if (isUpdatingPublishedTemplate || isUpdatingTemplate) {
            return;
        }
        setLeadStatusDropdownOpen(!leadStatusDropdownOpen);
    };

    const handleLeadStatusSelect = (statusValue, isChecked) => {
        console.log('handleLeadStatusSelect - statusValue:', statusValue, 'isChecked:', isChecked);
        
        setSelectedLeadStatus((prevSelected) => {
            let newSelected;
            
            if (isChecked) {
                // Add to array if not already present
                newSelected = prevSelected.includes(statusValue)
                    ? prevSelected
                    : [...prevSelected, statusValue];
            } else {
                // Remove from array
                newSelected = prevSelected.filter((status) => status !== statusValue);
            }
            
            return newSelected;
        });
    };

    const renderActionInput = (button, index) => {
       
        const getSelectedActionType = (btn) => {
            // PRIORITY 1: The most important rule. If the URL contains '/site-visit',
         
            const url = btn.websiteURL || btn.url || '';
            if (url.toLowerCase().includes('/site-visit')) {
                return 'site_visit';
            }
    
            // PRIORITY 2: If the button already has a `typeOfAction` from being
            // previously saved or from the user clicking "Add Button", we trust that value.
            if (btn.typeOfAction) {
                return btn.typeOfAction;
            }
    
            // PRIORITY 3 (Fallback): For data coming directly from the API that hasn't been
            // translated yet, we deduce the type from the raw `type` property.
            if (btn.type) {
                switch (btn.type) {
                    case 'URL':          return 'visit_website'; // Will only be hit if it's NOT a site visit URL.
                    case 'PHONE_NUMBER': return 'call_phone_number';
                    case 'COPY_CODE':    return 'copy_offer_code';
                    default:             return 'custom'; // For QUICK_REPLY from API.
                }
            }
    
            // If the button has no type information, default to custom.
            return 'custom';
        };
    
        // Use our reliable helper to get the one, true action type for the dropdown.
        const selectedType = getSelectedActionType(button);
    
        // The list of options available in the dropdown.
        const typeOfActions = [
            { label: "Marketing opt-out", value: "marketing_opt_out" },
            { label: "Visit Website", value: "visit_website" },
            { label: "Call Phone Number", value: "call_phone_number" },
            { label: "Copy Offer Code", value: "copy_offer_code" },
            { label: "Property Config", value: "property_config" },
            { label: "Site Visit", value: "site_visit" },
            { label: "Custom", value: "custom" },
        ];
    
        // The handler for when the user manually changes the dropdown.
        const handleChange = (e) => {
            // We update the button's `typeOfAction` to reflect the user's choice.
            const newButtonData = { ...button, typeOfAction: e.target.value };
            handleButtonChange(index, newButtonData);
        };
    
        return (
            <div className="w-40 pr-2 relative">
                <label htmlFor="typeOfAction" className="block text-xs font-medium text-gray-700 mb-1">
                    Action Type
                </label>
                <select
                    className="shadow appearance-none rounded w-full py-2 p-2 px-3 text-sm text-gray-700 leading-tight focus:outline-none focus:shadow-outline pr-8"
                    name="typeOfAction"
                    // The value of the dropdown is now correctly and reliably set.
                    value={selectedType}
                    onChange={handleChange}
                >
                    <option value="">Select Action</option>
                    {typeOfActions.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-2 top-5 flex items-center px-2 text-gray-700">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                </div>
            </div>
        );
    };

    const renderButtonInput = (button, index) => {
        const ButtonComponent = getTypeComponent(button.typeOfAction || button.type);

        if (!ButtonComponent) {
            return null;
        }

        return <ButtonComponent key={index} index={index} onChange={handleButtonChange} data={button} />;
    };

    // Fetch property configs when showPropertyConfig is enabled
    useEffect(() => {
        if (showPropertyConfig && project?.projectId) {
            fetchPropertyConfigs(project.projectId);
        }
    }, [showPropertyConfig, project]);


    const fetchPropertyConfigs = async (projectId) => {
        try {
            setLoadingConfigs(true);
            const response = await axiosInstance.get(`/building/property/config`, {
                params: {
                    projectIds: projectId
                }
            });
            if (response && Array.isArray(response)) {
                setPropertyConfigs(response);
            }
        } catch (error) {
            console.error("Error fetching property configurations:", error);
        } finally {
            setLoadingConfigs(false);
        }
    };

    // Handle property config selection
    const handlePropertyConfigSelect = (configId, isChecked) => {
        setSelectedPropertyConfigs(prev => {
            const newSelection = isChecked
                ? [...prev, configId]
                : prev.filter(id => id !== configId);

            // Update parent component state if available
            if (setPropertyConfigIds) {
                setPropertyConfigIds(newSelection);
            }

            return newSelection;
        });
    };

    // Function to check if it's a festival template
    const isFestivalTemplate = () => {
        const currentLeadType = localStorage.getItem('current_leadType');
        return currentLeadType === 'festival_template';
    };


    // Handle festival selection
    const handleFestivalSelect = (festival) => {
        setSelectedFestival(festival);
        setShowFestivalDropdown(false);
        // Automatically set the send date to festival date
        setSendDate(new Date(festival.date).toISOString().slice(0, 16));
    };

    // Add handleSendDateChange function
    const handleSendDateChange = (e) => {

        setSendDate(e.target.value);
    };

    // Function to handle template save (local storage)
    const handleTemplateSave = () => {
        try {
            const currentLeadType = localStorage.getItem('current_leadType') || 'standard';
            const templateType = isFestivalTemplate() ? 'FESTIVAL_TEMPLATE' :
                currentLeadType === 'birthday_template' ? 'BIRTHDAY_TEMPLATE' :
                    currentLeadType === 'reply_template' ? 'REPLY_NODE_TEMPLATE' :
                        'STANDARD_TEMPLATE';

            console.log('handleTemplateSave - selectedLeadStatus:', selectedLeadStatus);
            const templateData = {
                templateName,
                headerType,
                headerText,
                headerMedia,
                body,
                buttons,
                leadType: templateType === 'REPLY_NODE_TEMPLATE' ? [1, 2, 3, 4, 5] : leadType, // Default to all lead types for reply templates
                leadStatus: selectedLeadStatus.length > 0 ? selectedLeadStatus : null, // Include lead status
                variable: Object.keys(dummyValues).join(', '),
                dummyValues,
                conditions,
                conditionString,
                propertyConfigIds,
                templateType,
                sendDatetime: templateType === 'REPLY_NODE_TEMPLATE' ?
                    (initialData?.sendDatetime || null) : // Use inherited date for reply templates
                    (sendDate ? new Date(sendDate).toISOString() : null), // Use selected date for initial templates
                whatsappTemplateStatus: 'NOT_PUBLISHED',
                isPublished: false,
                isNotificationSent: false,
                order: 0,
                _metadata: {
                    leadType: currentLeadType,
                    templateType: templateType
                }
            };

            if (onTemplateCreate) {
                onTemplateCreate(templateData);
            }
        } catch (error) {
            console.error('Error saving template:', error);
        }
    };

    // Function to handle template updates (PUT request)
    const handleTemplateUpdate = async () => {
        if (!templateId && !isViewingSavedTemplate) {
            console.error('Cannot update: No template ID or not viewing saved template');
            return;
        }

        try {
            const currentLeadType = localStorage.getItem('current_leadType') || 'standard';
            const templateType = isFestivalTemplate() ? 'FESTIVAL_TEMPLATE' :
                currentLeadType === 'birthday_template' ? 'BIRTHDAY_TEMPLATE' :
                    currentLeadType === 'reply_template' ? 'REPLY_NODE_TEMPLATE' :
                        'STANDARD_TEMPLATE';

            console.log('handleTemplateUpdate - selectedLeadStatus:', selectedLeadStatus);
            const templateData = {
                templateName,
                headerType,
                headerText,
                headerMedia,
                body,
                buttons,
                leadType: templateType === 'REPLY_NODE_TEMPLATE' ? [1, 2, 3, 4, 5] : leadType, // Default to all lead types for reply templates
                leadStatus: selectedLeadStatus.length > 0 ? selectedLeadStatus : null, // Include lead status
                variable: Object.keys(dummyValues).join(', '),
                dummyValues,
                conditions,
                conditionString,
                propertyConfigIds,
                customerWhatsappTemplateId: templateId,
                templateType,
                sendDatetime: templateType === 'REPLY_NODE_TEMPLATE' ?
                    (initialData?.sendDatetime || null) : // Use inherited date for reply templates
                    (sendDate ? new Date(sendDate).toISOString() : null), // Use selected date for initial templates
                isUpdating: true,
                _metadata: {
                    leadType: currentLeadType,
                    templateType: templateType
                }
            };

            if (onUpdate) {
                await onUpdate(templateData);
            }
        } catch (error) {
            console.error('Error updating template:', error);
        }
    };

    // Function to handle published template updates (PUT request)
    const handlePublishedTemplateUpdate = async () => {
        if (!templateId) {
            console.error('Cannot update published template: No template ID');
            return;
        }

        // Prevent multiple simultaneous updates
        if (isUpdatingPublishedTemplate || isUpdatingTemplate) {
            return;
        }

        try {
            setIsUpdatingPublishedTemplate(true);
            
            // For published templates, we need to send all existing fields but only update specific ones
            // Get the current template data from the form state
            const updateData = {
                // Include all existing fields from the template
                customerWhatsappTemplateId: templateId,
                templateName: templateName,
                headerType: headerType,
                headerText: headerText,
                headerMedia: headerMedia,
                body: body,
                buttons: buttons,
                leadType: selectedLeadTypes, // This is the field being updated
                leadStatus: selectedLeadStatus.length > 0 ? selectedLeadStatus : null, // Include lead status
                conditions: conditions,
                conditionString: conditionString,
                whatsappTemplateStatus: 'PUBLISHED',
                templateType: templateType,
                sendDatetime: sendDate ? new Date(sendDate).toISOString() : null, // This is the field being updated
                isPublished: true,
                isNotificationSent: false,
                order: 0,
                // Include components to preserve existing content
                components: generateComponents(headerType, headerText, body, buttons, dummyValues),
                // Mark this as a published template update
                isUpdating: true,
                isPublished: true
            };

            // Call the parent onUpdate function which will handle the API call
            if (onUpdate) {
                await onUpdate(updateData);
            }
        } catch (error) {
            console.error('Error updating published template:', error);
            toast.error('Failed to update published template');
        } finally {
            setIsUpdatingPublishedTemplate(false);
        }
    };


    const handleTemplateCreate = async () => {
        // Don't trigger save/update when just opening the form
        if (!templateName && !headerText && !body) {
            return;
        }

        // Prevent multiple simultaneous operations
        if (isUpdatingTemplate || isUpdatingPublishedTemplate || isSubmitting) {
            return;
        }

        // Check if leads are selected (skip for reply templates)
        if (!selectedLeadTypes.length && isInitialNode && templateType !== 'REPLY_NODE_TEMPLATE') {
            toast.warning('Please select at least one lead type to proceed', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return; // Prevent saving if no leads are selected
        }

        try {
            if (templateId || isViewingSavedTemplate) {
                await handleTemplateUpdate();
            } else {
                handleTemplateSave();
            }
        } catch (error) {
            console.error('Error in handleTemplateCreate:', error);
        }
    };

    // Function to check if buttons should be shown
    const shouldShowButtons = () => {
        const currentLeadType = localStorage.getItem('current_leadType') || 'standard';

        return !isViewingSavedTemplate && !['birthday_template', 'festival_template'].includes(currentLeadType);
    };
    console.log("button shsow?????",shouldShowButtons());
    // Update this useEffect
    useEffect(() => {
        const hasSiteVisitButton = buttons.some(
            btn => btn.type === 'site_visit' || btn.typeOfAction === 'site_visit'
        );
        if (hasSiteVisitButton && setTemplateType) {
            setTemplateType('SITE_VISIT_TEMPLATE');
        } else if (setTemplateType) {
            setTemplateType('STANDARD_TEMPLATE');
        }
    }, [buttons, setTemplateType]);

    const validateForm = () => {
        const newErrors = {};

        // Template name validation
        if (!templateName.trim()) {
            newErrors.templateName = 'Template name is required';
        }

        // Lead type validation - Enhanced with warning (skip for reply templates)
        if (!selectedLeadTypes.length && templateType !== 'REPLY_NODE_TEMPLATE') {
            newErrors.leadType = 'Please select at least one lead type to proceed';
            // Show warning toast
            toast.warning('Please select at least one lead type to proceed', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        }

        // ... rest of validation code ...

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    if (showConditionBuilder && isInitialNode) {
        return (
            <div className="bg-white rounded-md p-2 mb-4">

                <ConditionBuilder
                    setConditionString={(str, conditions) => {
                        setConditionString(str);
                        setConditions(conditions);
                    }}
                    initialConditions={conditions}
                />
            </div>
        );
    }

    return (
        <div className='bg-white rounded-lg shadow-sm border p-3 mb-4 max-w-4xl'>

            {/* Template Name and Send Date in same row */}
            <div className='mb-6'>
                <div className="mb-4 grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Template Name Input */}
                    <div>
                        <label htmlFor="templateName" className="block text-gray-700 text-sm font-semibold mb-2">
                            Template Name <span className="text-red-500">*</span>
                        </label>
                        <CustomInput
                            type="text"
                            id="templateName"
                            placeholder="Enter template name"
                            value={templateName}
                            onChange={handleTemplateNameChange}
                            disabled={isUpdatingPublishedTemplate}
                            className={isUpdatingPublishedTemplate ? 'opacity-50 cursor-not-allowed' : ''}
                        />
                        {isUpdatingPublishedTemplate && (
                            <div className="mt-2 flex items-center gap-2 text-sm text-blue-600">
                                <Loader2 size={14} className="animate-spin" />
                                Updating template...
                            </div>
                        )}
                    </div>

                    {/* Festival Date - Only show for festival templates */}
                    {isFestivalTemplate() && (
                        <div>
                            <label htmlFor="sendDate" className="block text-gray-700 text-sm font-semibold mb-2">
                                Festival Date
                            </label>
                            <input
                                type="date"
                                id="sendDate"
                                value={sendDate ? new Date(sendDate).toISOString().split('T')[0] : ''}
                                onChange={(e) => {
                                    const date = new Date(e.target.value);
                                    date.setHours(0, 0, 0, 0);
                                    setSendDate(date.toISOString());
                                }}
                                disabled={isUpdatingPublishedTemplate}
                                className={`shadow cursor-auto appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${isUpdatingPublishedTemplate ? 'opacity-50 cursor-not-allowed' : ''}`}
                            />
                            {sendDate && (
                                <p className="mt-1 text-xs text-gray-500">
                                    Template will be sent on {new Date(sendDate).toLocaleDateString()}
                                </p>
                            )}
                            {isUpdatingPublishedTemplate && (
                                <div className="mt-2 flex items-center gap-2 text-sm text-blue-600">
                                    <Loader2 size={14} className="animate-spin" />
                                    Updating festival date...
                                </div>
                            )}
                        </div>
                    )}

                    {/* Send Date - Only show for initial nodes and not for festival templates */}
                    {isInitialNode && !isFestivalTemplate() && (
                        <div>
                            <label htmlFor="sendDate" className="block text-gray-700 text-sm font-semibold mb-2">
                                Send Date
                            </label>
                            <input
                                type="datetime-local"
                                id="sendDate"
                                value={sendDate || ''}
                                onChange={handleSendDateChange}
                                disabled={isUpdatingPublishedTemplate}
                                className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${isUpdatingPublishedTemplate ? 'opacity-50 cursor-not-allowed' : ''}`}
                                min={new Date().toISOString().slice(0, 16)}
                            />
                            {sendDate && (
                                <p className="mt-1 text-xs text-gray-500">
                                    Template will be sent on {new Date(sendDate).toLocaleString()}
                                </p>
                            )}
                            {isUpdatingPublishedTemplate && (
                                <div className="mt-2 flex items-center gap-2 text-sm text-blue-600">
                                    <Loader2 size={14} className="animate-spin" />
                                    Updating send date...
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Lead Type and Lead Status in same row */}
                {isInitialNode && templateType !== 'REPLY_NODE_TEMPLATE' && (
                    <div className="mb-4 grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Lead Type Selection - Enhanced UI */}
                        <div>
                            <label htmlFor="leadType" className="block text-gray-700 text-sm font-semibold mb-2">
                                Lead Type <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <button
                                    type="button"
                                    onClick={toggleDropdown}
                                    disabled={isUpdatingPublishedTemplate}
                                    className={`w-full py-2.5 px-3 text-left border rounded-md text-sm text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex items-center gap-2 transition-all duration-200 ${errors.leadType
                                        ? 'border-red-500 bg-red-50'
                                        : selectedLeadTypes.length > 0
                                            ? 'border-green-500 bg-green-50'
                                            : 'border-gray-300 hover:border-blue-500'
                                        } ${isUpdatingPublishedTemplate ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    <span className="flex-1">
                                        {selectedLeadTypes.length > 0
                                            ? `${selectedLeadTypes.length} lead type(s) selected`
                                            : 'Select Lead Types'}
                                    </span>

                                    <ChevronDown size={14} className={`text-gray-600 transition-transform duration-200 ${dropdownOpen ? 'transform rotate-180' : ''}`} />
                                </button>
                                {dropdownOpen && !isUpdatingPublishedTemplate && (
                                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-md text-sm shadow-lg max-h-48 overflow-y-auto">
                                        <div className="p-2 bg-gray-50 border-b">
                                            <p className="text-xs text-gray-600">Select one or more lead types</p>
                                        </div>
                                        {leadTypeOptions.map((option) => (
                                            <label
                                                key={option.value}
                                                className="flex items-center px-3 py-2 hover:bg-blue-50 cursor-pointer transition-colors duration-150"
                                            >
                                                <input
                                                    type="checkbox"
                                                    value={option.value}
                                                    checked={selectedLeadTypes.includes(option.id)}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        handleLeadTypeSelect(option.value, e.target.checked);
                                                    }}
                                                    className="mr-2 h-3 w-3 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                                />
                                                <div>
                                                    <span className="text-gray-700 text-sm font-medium">{option.label}</span>
                                                    <p className="text-xs text-gray-500 mt-0.5">
                                                        {option.value === 'COLD_LEAD' && 'New leads with no prior interaction'}
                                                        {option.value === 'LUKE_WARM_LEAD' && 'Leads with some initial interest'}
                                                        {option.value === 'WARM_LEAD' && 'Leads actively considering'}
                                                        {option.value === 'HOT_LEAD' && 'Leads ready to make a decision'}
                                                        {option.value === 'CUSTOMER' && 'Existing customers'}
                                                    </p>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                )}
                                {selectedLeadTypes.length === 0 && (
                                    <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full">
                                        Please select at least one lead type
                                    </span>
                                )}
                            </div>
                            {errors.leadType && (
                                <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                    {errors.leadType}
                                </p>
                            )}
                            {/* Selected Lead Types as Chips */}
                            <div className="flex flex-wrap mt-2 gap-1.5">
                                {selectedLeadTypes.map((typeId) => {
                                    const leadTypeOption = leadTypeOptions.find(option => option.id === typeId);
                                    const label = leadTypeOption ? leadTypeOption.label : `Unknown ID: ${typeId}`;
                                    return (
                                        <div
                                            key={typeId}
                                            className="bg-flamingo-50 text-flamingo-500 text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1 border border-flamingo-200"
                                        >
                                            <span>{label}</span>
                                            <button
                                                type="button"
                                                onClick={() => handleLeadTypeSelect(typeId, false)}
                                                disabled={isUpdatingPublishedTemplate}
                                                className={`text-flamingo-500 hover:text-flamingo-600 hover:bg-flamingo-100 rounded-full p-0.5 transition-colors duration-150 ${isUpdatingPublishedTemplate ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            >
                                                <XCircle size={12} />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                            {isUpdatingPublishedTemplate && (
                                <div className="mt-2 flex items-center gap-2 text-sm text-blue-600">
                                    <Loader2 size={14} className="animate-spin" />
                                    Updating lead types...
                                </div>
                            )}
                        </div>

                        {/* Lead Status Selection - Enhanced UI */}
                        <div>
                            <label htmlFor="leadStatus" className="block text-gray-700 text-sm font-semibold mb-2">
                                Lead Status
                            </label>
                            <div className="relative">
                                <button
                                    type="button"
                                    onClick={toggleLeadStatusDropdown}
                                    disabled={isUpdatingPublishedTemplate}
                                    className={`w-full py-2.5 px-3 text-left border rounded-md text-sm text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex items-center gap-2 transition-all duration-200 ${
                                        selectedLeadStatus.length > 0
                                            ? 'border-green-500 bg-green-50'
                                            : 'border-gray-300 hover:border-blue-500'
                                    } ${isUpdatingPublishedTemplate ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    <span className="flex-1">
                                        {selectedLeadStatus.length > 0
                                            ? `${selectedLeadStatus.length} lead status(es) selected`
                                            : 'Select Lead Status'}
                                    </span>
                                    <ChevronDown size={14} className={`text-gray-600 transition-transform duration-200 ${leadStatusDropdownOpen ? 'transform rotate-180' : ''}`} />
                                </button>
                                {leadStatusDropdownOpen && !isUpdatingPublishedTemplate && (
                                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-md text-sm shadow-lg max-h-48 overflow-y-auto">
                                        <div className="p-2 bg-gray-50 border-b">
                                            <p className="text-xs text-gray-600">Select one or more lead statuses</p>
                                        </div>
                                        {leadStatusOptions.map((option) => (
                                            <label
                                                key={option.value}
                                                className="flex items-center px-3 py-2 hover:bg-blue-50 cursor-pointer transition-colors duration-150"
                                            >
                                                <input
                                                    type="checkbox"
                                                    value={option.value}
                                                    checked={selectedLeadStatus.includes(option.value)}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        handleLeadStatusSelect(option.value, e.target.checked);
                                                    }}
                                                    className="mr-2 h-3 w-3 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                                />
                                                <div className="flex items-center gap-2">
                                                    <div className={`w-2 h-2 rounded-full ${option.dotColor}`}></div>
                                                    <span className="text-gray-700 text-sm font-medium">{option.label}</span>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {/* Selected Lead Statuses as Chips */}
                            <div className="flex flex-wrap mt-2 gap-1.5">
                                {selectedLeadStatus.map((statusValue) => {
                                    const leadStatusOption = leadStatusOptions.find(option => option.value === statusValue);
                                    const label = leadStatusOption ? leadStatusOption.label : `Unknown: ${statusValue}`;
                                    const dotColor = leadStatusOption ? leadStatusOption.dotColor : 'bg-gray-400';
                                    return (
                                        <div
                                            key={statusValue}
                                            className="bg-blue-50 text-blue-500 text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1 border border-blue-200"
                                        >
                                            <div className={`w-2 h-2 rounded-full ${dotColor}`}></div>
                                            <span>{label}</span>
                                            <button
                                                type="button"
                                                onClick={() => handleLeadStatusSelect(statusValue, false)}
                                                disabled={isUpdatingPublishedTemplate}
                                                className={`text-blue-500 hover:text-blue-600 hover:bg-blue-100 rounded-full p-0.5 transition-colors duration-150 ${isUpdatingPublishedTemplate ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            >
                                                <XCircle size={12} />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                            {isUpdatingPublishedTemplate && (
                                <div className="mt-2 flex items-center gap-2 text-sm text-blue-600">
                                    <Loader2 size={14} className="animate-spin" />
                                    Updating lead status...
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Conditions Section - Only show for initial node */}
                {/* {isInitialNode && (
                    <div className="mb-4">
                        {conditions.length > 0 ? (
                            <div className="bg-white border rounded-lg p-4">
                                <div className="flex justify-between items-center mb-3">
                                    <h3 className="text-md font-semibold text-gray-700">Template Conditions</h3>
                                    <button
                                        onClick={() => setShowConditionBuilder(true)}
                                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                    >
                                        Edit Conditions
                                    </button>
                                </div>
                                <div className="bg-gray-50 p-3 rounded">
                                    <p className="text-sm font-mono">{conditionString}</p>
                                </div>
                            </div>
                        ) : (
                            <button
                                onClick={() => setShowConditionBuilder(true)}
                                className="w-full py-3 px-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:text-gray-800 hover:border-gray-400 transition-colors"
                            >
                                + Add Conditions
                            </button>
                        )}
                    </div>
                )} */}


            </div>

            {/* Content Section - Only show for non-published templates */}
            {!isPublishedTemplate && (
                <>
                    <div className="mb-4 p-4 rounded-lg">
                        <label htmlFor="templateName" className="block text-gray-700 text-sm font-semibold mb-2">
                            Content
                        </label>
                        <label className="flex text-xs font-normal text-slate-500 mb-3 text-nowrap items-center gap-1">
                            Fill in header, and body sections of your template.
                        </label>
                        <div className="mb-4 relative">
                            <label htmlFor="headerType" className="block text-gray-700 text-sm font-medium mb-2">
                                Header Type
                            </label>
                            <div className="relative w-full sm:w-1/2">
                                <button
                                    type="button"
                                    className="w-full py-2 px-3 pr-8 text-left border rounded-md text-sm text-gray-700 shadow-sm appearance-none focus:outline-none focus:shadow-outline flex items-center gap-2"
                                    onClick={() => setIsOpen(!isOpen)}
                                >
                                    {selectedOption?.icon}
                                    {selectedOption?.label}
                                    <div className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none">
                                        <ChevronDown size={14} />
                                    </div>
                                </button>

                                {isOpen && (
                                    <div className="absolute z-10 mt-1 w-full bg-white border rounded-md shadow-lg text-sm">
                                        {headerOptions.map((option) => (
                                            <button
                                                key={option.value}
                                                onClick={() => handleHeaderTypeChange(option.value)}
                                                className={`w-full px-3 py-2 flex items-center gap-2 hover:bg-blue-50 transition-colors ${option.value === headerType ? "bg-blue-100" : ""}`}
                                            >
                                                {option.icon}
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {headerType === 'text' && (
                                <>
                                    <textarea
                                        id="headerText"
                                        className="shadow-sm text-sm mt-3 appearance-none border rounded-md w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline h-16 resize-vertical"
                                        placeholder="Add a header (max 60 chars) ...."
                                        value={headerText}
                                        onChange={handleHeaderTextChange}
                                    />
                                </>
                            )}

                            {headerType !== 'text' && (
                                <>
                                    <label htmlFor="headerMedia" className="block text-gray-700 text-sm font-bold mb-2">
                                        Upload {headerType}
                                    </label>
                                    <input
                                        type="file"
                                        id="headerMedia"
                                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                        onChange={handleImageChange}
                                        accept={headerType === 'image' ? 'image/*' : headerType === 'video' ? 'video/*' : '.pdf,.doc,.docx'}
                                    />
                                    {selectedImage && (
                                        <div className="mt-2 relative">
                                            <img src={selectedImage} alt="Header Preview" className="max-w-xs max-h-48 object-contain rounded" />
                                            <button
                                                onClick={handleRemoveImage}
                                                className="absolute top-0 right-0 bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-2 rounded"
                                            >
                                                X
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        <div className="mb-3 relative">
                            <label className="flex text-xs font-normal text-slate-500 mb-2 text-nowrap items-center gap-1">
                                Choose variable
                            </label>
                            <div className="relative inline-block">
                                <button
                                    onClick={toggleVariableDropdown}
                                    className="bg-white border border-gray-200 text-flamingo-500 hover:bg-gray-300 font-medium py-2 px-8 rounded flex items-center gap-2 text-sm">
                                    Select Variable
                                    <ChevronDown size={14} />
                                </button>

                                {showVariableDropdown && (
                                    <div className="absolute left-0 bottom-full mb-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none text-sm">
                                        <div className="py-1">
                                            {variableOptions.map((option) => (
                                                <button
                                                    key={option.value}
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        insertVariable(option.value);
                                                    }}
                                                    className="block w-full text-left px-3 py-2 hover:bg-gray-100 transition-colors"
                                                >
                                                    <div className="text-gray-800 text-sm">{option.label}</div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Dummy Values Input Section */}
                        {Object.keys(dummyValues).length > 0 && (
                            <div className="mb-4">
                                <label className="block text-gray-700 text-sm font-medium mb-2">
                                    Example Values for Variables
                                </label>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {Object.keys(dummyValues).map((varName) => (
                                        <div key={varName}>
                                            <label className="block text-xs text-gray-600 mb-1">
                                                {varName}:
                                            </label>
                                            <input
                                                type="text"
                                                value={dummyValues[varName]}
                                                onChange={(e) => handleDummyValueChange(varName, e.target.value)}
                                                className="shadow-sm appearance-none border rounded-md w-full py-1.5 px-3 text-sm text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                                placeholder={`Enter example value for ${varName}`}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="mb-4 relative flex-1">
                            <label htmlFor="body" className="block text-gray-700 text-sm font-semibold mb-2">
                                Body (Max 1024 chars)
                            </label>

                            <ReactQuill
                                theme="snow"
                                value={body}
                                onChange={handleBodyChange}
                                modules={modules}
                                formats={formats}
                                placeholder="Add a body..."
                                style={{ height: '150px', marginBottom: '10px' }}
                                ref={quillRef}
                            />
                        </div>
                    </div>

                    {/* Button Section - Only show if template type allows buttons */}
                    {shouldShowButtons() && (
                        <div className="mb-4 rounded-md bg-white p-4">
                            <label className="block text-gray-700 text-md font-semibold mb-2">Buttons (Optional)</label>
                            <p className="text-gray-600 text-sm mb-2">
                                Create buttons that let customers respond to your message or take action. You can add up to ten buttons.
                                If you add more than three buttons, they will appear in a list.
                            </p>

                            <div className="relative inline-block mb-3">
                                <button
                                    onClick={toggleButtonDropdown}
                                    className="bg-white-200 hover:bg-gray-300 text-flamingo-500 text-sm border transition-all duration-200 border-gray-200 font-medium py-2 px-8 rounded  flex items-center gap-2">
                                    + Add button
                                    <ChevronDown size={16} />
                                </button>

                                {showButtonDropdown && (
                                    <div className="absolute left-0 bottom-full mb-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none ">
                                        <div className="py-2 px-3 font-semibold text-gray-900 border-b text-sm">Quick reply buttons</div>
                                        <div className="py-1">
                                            <button
                                                onClick={() => addButton('marketing_opt_out')}
                                                className="block w-full text-left px-3 py-2 hover:bg-gray-100 transition-colors"
                                                disabled={isTotalButtonLimitReached()}
                                            >
                                                <div className="text-gray-800 text-sm">Marketing opt-out</div>
                                                <div className="text-xs text-gray-500">Recommended</div>
                                            </button>
                                            <button
                                                onClick={() => addButton('custom')}
                                                className="block w-full text-left px-3 py-2 hover:bg-gray-100 transition-colors"
                                                disabled={isTotalButtonLimitReached()}
                                            >
                                                <div className="text-gray-800 text-sm">Custom Quick Reply</div>
                                            </button>
                                        </div>

                                        <div className="py-2 px-3 font-semibold text-gray-900 border-b mt-2 text-xs">Call-to-action buttons</div>
                                        <div className="py-1">
                                            <button
                                                onClick={() => addButton('visit_website')}
                                                className={`block w-full text-left px-3 py-2 hover:bg-gray-100 transition-colors ${isButtonTypeAtLimit('visit_website') ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                disabled={isButtonTypeAtLimit('visit_website') || isTotalButtonLimitReached()}
                                            >
                                                <div className="text-gray-800 text-sm">Visit website</div>
                                                <div className="text-xs text-gray-500">2 buttons maximum</div>
                                            </button>
                                            <button
                                                onClick={() => addButton('site_visit')}
                                                className={`block w-full text-left px-3 py-2 hover:bg-gray-100 transition-colors ${isButtonTypeAtLimit('visit_website') ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                disabled={isButtonTypeAtLimit('visit_website') || isTotalButtonLimitReached()}
                                            >
                                                <div className="text-xs text-gray-500">Visit scheduling link</div>
                                            </button>
                                            <button
                                                onClick={() => addButton('call_phone_number')}
                                                className={`block w-full text-left px-3 py-2 hover:bg-gray-100 transition-colors ${isButtonTypeAtLimit('call_phone_number') ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                disabled={isButtonTypeAtLimit('call_phone_number') || isTotalButtonLimitReached()}
                                            >
                                                <div className="text-gray-800 text-sm">Call Phone Number</div>
                                                <div className="text-xs text-gray-500">1 button maximum</div>
                                            </button>

                                            <button
                                                onClick={() => addButton('copy_offer_code')}
                                                className={`block w-full text-left px-3 py-2 hover:bg-gray-100 transition-colors ${isButtonTypeAtLimit('copy_offer_code') ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                disabled={isButtonTypeAtLimit('copy_offer_code') || isTotalButtonLimitReached()}
                                            >
                                                <div className="text-gray-800 text-sm">Copy offer code</div>
                                                <div className="text-xs text-gray-500">1 button maximum</div>
                                            </button>

                                            <button
                                                onClick={() => addButton('property_config')}
                                                className="block w-full text-left px-3 py-2 hover:bg-gray-100 transition-colors"
                                                disabled={isTotalButtonLimitReached()}
                                            >
                                                <div className="text-gray-800 text-sm">Property Configuration</div>
                                                <div className="text-xs text-gray-500">Select a property configuration</div>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="mt-3 space-y-2">
                                {buttons.map((button, index) => (
                                    <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-md border border-gray-200 shadow-sm">
                                        {renderActionInput(button, index)}
                                        <div className="flex-1">
                                            {button.typeOfAction === 'marketing_opt_out' &&
                                                <div className="flex items-center h-full">
                                                    <span className="text-sm text-gray-600">Quick Reply: {button.label}</span>
                                                </div>
                                            }
                                            {button.typeOfAction === 'custom' && (
                                                <div>
                                                    <label className="block text-sm text-gray-600 mb-1">Quick Reply Text:</label>
                                                    <input
                                                        type="text"
                                                        value={button.buttonText}
                                                        onChange={(e) => {
                                                            const value = e.target.value.slice(0, 25); // Limit to 25 characters
                                                            const newButton = { ...button, buttonText: value, label: value };
                                                            handleButtonChange(index, newButton);
                                                        }}
                                                        maxLength={25}
                                                        className="shadow appearance-none border rounded w-full py-1.5 px-3 text-sm text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                                        placeholder="Enter quick reply text..."
                                                    />
                                                    {button.buttonText && button.buttonText.length > 25 && (
                                                        <div className="text-xs text-red-600 mt-1">
                                                            ⚠️ Button text exceeds 25 characters (current: {button.buttonText.length})
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {button.typeOfAction === 'property_config' && (
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-600 mb-1">Property Configuration:</label>
                                                    {propertyConfigs.length === 0 ? (
                                                        <div className="space-y-2">
                                                            <div className="text-sm text-yellow-600 p-2 bg-yellow-50 rounded">
                                                                {loadingConfigs ?
                                                                    "Loading configurations..." :
                                                                    "No property configurations available. You can still create a property button and set the config ID manually."}
                                                            </div>
                                                            <div className="space-y-2">
                                                                <input
                                                                    type="number"
                                                                    value={button.configId || ""}
                                                                    onChange={(e) => {
                                                                        const configId = parseInt(e.target.value);
                                                                        if (!isNaN(configId)) {
                                                                            const newButton = {
                                                                                ...button,
                                                                                configId: configId,
                                                                                buttonText: (button.buttonText || `Interested in Config #${configId}`).slice(0, 25),
                                                                                label: (button.buttonText || `Interested in Config #${configId}`).slice(0, 25)
                                                                            };
                                                                            handleButtonChange(index, newButton);
                                                                        }
                                                                    }}
                                                                    className="shadow appearance-none border rounded w-full py-1.5 px-3 text-sm text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                                                    placeholder="Enter Config ID manually..."
                                                                    min="1"
                                                                />
                                                                <input
                                                                    type="text"
                                                                    value={button.buttonText || ''}
                                                                    onChange={(e) => {
                                                                        const value = e.target.value.slice(0, 25); // Limit to 25 characters
                                                                        const newButton = { ...button, buttonText: value, label: value };
                                                                        handleButtonChange(index, newButton);
                                                                    }}
                                                                    className="shadow appearance-none border rounded w-full py-1.5 px-3 text-sm text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                                                    placeholder="Button text..."
                                                                    maxLength={25}
                                                                />
                                                                {button.buttonText && button.buttonText.length > 25 && (
                                                                    <div className="text-xs text-red-600 mt-1">
                                                                        ⚠️ Button text exceeds 25 characters (current: {button.buttonText.length})
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="space-y-2">
                                                            <select
                                                                className="shadow appearance-none border rounded w-full py-1.5 px-3 text-sm text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                                                value={button.configId || ""}
                                                                onChange={(e) => {
                                                                    const configId = parseInt(e.target.value);
                                                                    const config = propertyConfigs.find(item => item.propertyConfig.propertyConfigId === configId);
                                                                    const configName = config ? (config.propertyConfig.configName || `Config #${configId}`) : '';
                                                                    const newButton = {
                                                                        ...button,
                                                                        configId: configId,
                                                                        buttonText: `Interested in ${configName}`.slice(0, 25),
                                                                        label: `Interested in ${configName}`.slice(0, 25)
                                                                    };
                                                                    handleButtonChange(index, newButton);
                                                                }}
                                                            >
                                                                <option value="">Select a Configuration</option>
                                                                {propertyConfigs.map((item) => (
                                                                    <option
                                                                        key={item.propertyConfig.propertyConfigId}
                                                                        value={item.propertyConfig.propertyConfigId}
                                                                    >
                                                                        {item.propertyConfig.configName || `Config #${item.propertyConfig.propertyConfigId}`}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            {button.configId && (
                                                                <input
                                                                    type="text"
                                                                    value={button.buttonText}
                                                                    onChange={(e) => {
                                                                        const value = e.target.value.slice(0, 25); // Limit to 25 characters
                                                                        const newButton = { ...button, buttonText: value, label: value };
                                                                        handleButtonChange(index, newButton);
                                                                    }}
                                                                    maxLength={25}
                                                                    className="shadow appearance-none border rounded w-full py-1.5 px-3 text-sm text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                                                    placeholder="Button text..."
                                                                />
                                                            )}
                                                          
                                                                <div className="text-xs text-gray-500 mt-1">
                                                                    Button text should be less than 25 characters.({button.buttonText.length}/25)
                                                                </div>
                                                            
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {renderButtonInput(button, index)}
                                        </div>
                                        <button
                                            onClick={() => removeButton(index)}
                                            className="text-red-500 hover:text-red-700 p-1 hover:bg-red-50 rounded"
                                        >
                                            <XCircle size={18} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Published Template Notice */}
            {isPublishedTemplate && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <h3 className="text-sm font-semibold text-blue-800">Published Template</h3>
                        {isUpdatingPublishedTemplate && (
                            <div className="flex items-center gap-2 text-blue-600">
                                <Loader2 size={14} className="animate-spin" />
                                <span className="text-xs">Updating...</span>
                            </div>
                        )}
                    </div>
                    <p className="text-sm text-blue-700">
                        This template is published. You can only edit the template name, send date, and lead types. 
                        Other content fields are locked to maintain consistency.
                        {isUpdatingPublishedTemplate && (
                            <span className="block mt-2 font-medium">
                                Please wait while we update your template...
                            </span>
                        )}
                    </p>
                </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">



              

                <PrimaryButton
                    onClick={isPublishedTemplate ? handlePublishedTemplateUpdate : handleTemplateCreate}
                    disabled={isUpdatingTemplate || isUpdatingPublishedTemplate}
                >
                    {isUpdatingTemplate || isUpdatingPublishedTemplate ? (
                        <>
                            <Loader2 size={16} className="mr-2 animate-spin" />
                            {isPublishedTemplate ? 'Updating Published Template...' : 'Updating Template...'}
                        </>
                    ) : (
                        isPublishedTemplate ? 'Update Published Template' : 
                        isViewingSavedTemplate ? 'Update Template' : 'Save Template'
                    )}
                </PrimaryButton>
            </div>
        </div>
    );
}

// Helper function to generate components for the request body
const generateComponents = (headerType, headerText, body, buttons, dummyValues) => {
    const components = [];

    if (headerType === 'text' && headerText) {
        components.push({ type: 'HEADER', format: 'TEXT', text: headerText });
    }

    if (body) {
        // Format the body text according to WhatsApp guidelines
        const formattedBody = formatWhatsappText(body);
        const bodyComponent = { type: 'BODY', text: formattedBody };
        const variableMatches = body.match(/{{([^}]+)}}/g);

        if (variableMatches) {
            bodyComponent.example = {
                body_text: [variableMatches.map(match => {
                    const varName = match.replace(/[{}]/g, '');
                    return dummyValues[varName] || '';
                })]
            };
        }
        components.push(bodyComponent);
    }

    if (buttons.length > 0) {
        components.push({
            type: 'BUTTONS',
            buttons: buttons.map(button => ({
                type: button.type || 'QUICK_REPLY',
                text: button.label || button.buttonText
            }))
        });
    }

    return components;
};

// Helper function to format WhatsApp text
const formatWhatsappText = (text) => {
    // Remove HTML tags and convert to plain text
    const plainText = text.replace(/<[^>]*>/g, '');
    
    // Convert common HTML entities
    const decodedText = plainText
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'");
    
    return decodedText.trim();
};

export default TemplateForm;